import { ApolloClient, InMemoryCache, split } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { getMainDefinition } from '@apollo/client/utilities';
import { WebSocketLink } from 'apollo-link-ws';
import { GraphQLClient } from 'graphql-request';
import { isEmpty } from 'lodash';
import {
  ALLOWED_UPLOAD_RESOLVERS,
  UPLOAD_ENDPOINT_PATH,
} from 'utils/constants';

import { environment, isServerSide } from './dataLayer/Environment';
import { getSdk } from './generated/graphql';

const getClient = () => {
  const urlPrefix = (environment?.nextauthUrl || '') as string;
  if (isServerSide && environment.isDocker)
    urlPrefix.replace('localhost', 'host.docker.internal');
  const url = urlPrefix + `/gql${environment.isDev() ? '-dev' : ''}`;
  const client = new GraphQLClient(url);
  // Use BatchHttpLink so multiple queries fired within a 10ms window are
  // sent as a single HTTP POST. Dramatically reduces request waterfalling
  // in production where browser <-> server RTT is high.
  const httpLink = new BatchHttpLink({
    uri: url,
    batchInterval: 10,
    batchMax: 50,
    credentials: 'include',
  });

  // On the client we still need a WebSocket link for GraphQL subscriptions
  // (see useActiveUser / useConversations). Queries and mutations should
  // NOT go over the WebSocket — that serializes them and is the main cause
  // of slow page loads in production.
  const link = isServerSide
    ? httpLink
    : split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
          );
        },
        new WebSocketLink({
          uri: `${environment?.nextPublicSocketUrl}`,
          options: {
            reconnect: true,
          },
        }) as any,
        httpLink
      );
  const apolloClient = new ApolloClient({
    uri: url,
    cache: new InMemoryCache(),
    link: link as any,
    ssrMode: isServerSide,
  });

  const sdk = getSdk(client);
  const uploadClient = new GraphQLClient(UPLOAD_ENDPOINT_PATH);
  const sdkUpload = getSdk(uploadClient);

  ALLOWED_UPLOAD_RESOLVERS.forEach((e) => {
    (sdk as any)[e] = (sdkUpload as any)[e];
  });

  (sdk as any).setHeader = (string: any, cookie: any) => {
    client.setHeader(string, cookie);
  };

  (sdk as any).apolloClient = apolloClient;

  return sdk as typeof sdk & {
    apolloClient: typeof apolloClient;
  };
};

export const makeHeaders = ({ token }: { token?: string }) => {
  const requestHeaders: {
    Authorization?: string;
  } = {};

  if (token) {
    requestHeaders.Authorization = `Bearer ${token}`;
  }

  return !isEmpty(requestHeaders) ? requestHeaders : undefined;
};

export const backendClient = getClient();

export default backendClient;
